import type { RoleData, UserRoleType } from '@/lib/types';
import { generateNumericId } from '@/lib/utils';

interface Permission {
  id: string;
  resource: string;
  action: string;
}

interface Role {
  id: string;
  name: UserRoleType;
  permissions: Permission[];
  isSystemRole: boolean;
  description: string;
}

/**
 * Role manager with predefined role templates
 */
export class RoleManager {
  private static readonly ROLE_TEMPLATES: Record<UserRoleType, string[]> = {
    'Super Admin': [
      'users.view', 'users.create', 'users.edit', 'users.delete',
      'spaces.view', 'spaces.create', 'spaces.edit', 'spaces.delete',
      'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete',
      'analytics.view', 'analytics.financial',
      'platform.manage', 'organizations.manage'
    ],
    'Admin': [
      'users.view', 'users.create', 'users.edit',
      'spaces.view', 'spaces.create', 'spaces.edit',
      'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete',
      'analytics.view'
    ],
    'Staff': [
      'users.view', 'users.edit',
      'spaces.view',
      'bookings.view', 'bookings.create', 'bookings.edit',
      'analytics.view_basic'
    ],
    'Member': [
      'bookings.view', 'bookings.create',
      'spaces.view'
    ],
    'Guest': [
      'spaces.view',
      'bookings.view'
    ],
    'Teacher': [
      'users.view',
      'spaces.view',
      'bookings.view', 'bookings.create', 'bookings.edit',
      'analytics.view_basic'
    ],
    'Tennis School': [
      'users.view', 'users.create', 'users.edit',
      'spaces.view', 'spaces.edit',
      'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete',
      'analytics.view'
    ],
    'Tennis Club': [
      'users.view', 'users.create', 'users.edit',
      'spaces.view', 'spaces.edit',
      'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete',
      'analytics.view'
    ]
  } as const;

  private roles: Role[] = [];

  constructor() {
    this.initializeSystemRoles();
  }

  /**
   * Initialize system roles from templates
   */
  private initializeSystemRoles(): void {
    this.roles = Object.entries(RoleManager.ROLE_TEMPLATES).map(([roleName, permissionStrings]) => ({
      id: generateNumericId(10),
      name: roleName as UserRoleType,
      permissions: permissionStrings.map(this.createPermission),
      isSystemRole: true,
      description: `${roleName} role with predefined permissions`,
    }));
  }

  /**
   * Create permission object from string
   */
  private createPermission(permissionString: string): Permission {
    const [resource, action] = permissionString.split('.');
    return {
      id: generateNumericId(8),
      resource,
      action,
    };
  }

  /**
   * Get all roles (excluding Super Admin for UI)
   */
  async getRoles(): Promise<RoleData[]> {
    const filteredRoles = this.roles.filter(role => role.name !== 'Super Admin');
    
    return filteredRoles.map(role => ({
      name: role.name,
      description: role.description,
      isSystemRole: role.isSystemRole,
      permissions: role.permissions.map(p => `${p.resource}.${p.action}`),
    }));
  }

  /**
   * Add a new custom role
   */
  async addRole(roleName: UserRoleType, description = ''): Promise<RoleData[] | null> {
    // Check if role already exists
    if (this.roles.some(r => r.name.toLowerCase() === roleName.toLowerCase())) {
      return null;
    }

    const newRole: Role = {
      id: generateNumericId(10),
      name: roleName,
      permissions: [], // Start with no permissions
      isSystemRole: false,
      description: description || `Custom ${roleName} role`,
    };

    this.roles.push(newRole);
    return this.getRoles();
  }

  /**
   * Delete a custom role (system roles cannot be deleted)
   */
  async deleteRole(roleName: string): Promise<RoleData[] | null> {
    const roleIndex = this.roles.findIndex(r => 
      r.name.toLowerCase() === roleName.toLowerCase() && !r.isSystemRole
    );

    if (roleIndex === -1) {
      return null; // Role not found or is system role
    }

    this.roles.splice(roleIndex, 1);
    return this.getRoles();
  }

  /**
   * Get permissions for a specific role
   */
  async getRolePermissions(roleName: UserRoleType): Promise<string[]> {
    const role = this.roles.find(r => r.name === roleName);
    return role ? role.permissions.map(p => `${p.resource}.${p.action}`) : [];
  }

  /**
   * Update permissions for a role
   */
  async updateRolePermissions(roleName: string, permissions: string[]): Promise<boolean> {
    const role = this.roles.find(r => r.name.toLowerCase() === roleName.toLowerCase());
    
    if (!role || role.isSystemRole) {
      return false; // Cannot update system roles
    }

    role.permissions = permissions.map(this.createPermission);
    return true;
  }

  /**
   * Check if a role has a specific permission
   */
  hasPermission(roleName: UserRoleType, permission: string): boolean {
    const role = this.roles.find(r => r.name === roleName);
    if (!role) return false;

    return role.permissions.some(p => `${p.resource}.${p.action}` === permission);
  }

  /**
   * Get all available permissions
   */
  getAvailablePermissions(): string[] {
    const allPermissions = new Set<string>();
    
    Object.values(RoleManager.ROLE_TEMPLATES).forEach(permissions => {
      permissions.forEach(permission => allPermissions.add(permission));
    });

    return Array.from(allPermissions).sort();
  }

  /**
   * Validate permissions against available set
   */
  validatePermissions(permissions: string[]): { valid: boolean; invalid: string[] } {
    const available = new Set(this.getAvailablePermissions());
    const invalid = permissions.filter(p => !available.has(p));
    
    return {
      valid: invalid.length === 0,
      invalid,
    };
  }

  /**
   * Get role statistics
   */
  getStats(): {
    totalRoles: number;
    systemRoles: number;
    customRoles: number;
    totalPermissions: number;
  } {
    const systemRoles = this.roles.filter(r => r.isSystemRole).length;
    const totalPermissions = this.getAvailablePermissions().length;

    return {
      totalRoles: this.roles.length,
      systemRoles,
      customRoles: this.roles.length - systemRoles,
      totalPermissions,
    };
  }

  /**
   * Reset to default system roles
   */
  reset(): void {
    this.initializeSystemRoles();
  }
}

// Export singleton instance
export const roleManager = new RoleManager(); 
